import { ExternalLinkIcon } from "@chakra-ui/icons";
import { Image } from "@chakra-ui/react";
import React from "react";
import {
  RiAppsLine,
  RiArrowLeftLine,
  RiBuilding4Line,
  RiCalendarTodoLine,
  RiChat2Line,
  RiCurrencyLine,
  RiDashboard2Line,
  RiFlaskLine,
  RiHomeLine,
  RiNotification4Line,
  RiQuestionLine,
  RiShieldKeyholeLine,
  RiShuffleLine,
  RiSoundModuleLine,
  RiTeamLine,
  RiTreasureMapLine,
  RiUserLine,
  RiUserSettingsLine,
} from "react-icons/ri";
import { useLocation } from "react-router-dom";
import { useFormImportFlow } from "src/components/FormImport/FormImportFlowContext";
import { FlowStatus } from "src/components/FormImport/state/constants";
import { BaseLayout } from "src/components/Layout/BaseLayout";
import { Header } from "src/components/Layout/Header";
import {
  LeftNav,
  NavGroup,
  NavItem,
  NavLinkItem,
} from "src/components/Navigation";
import { NavEnrollmentPeriodDropdown } from "src/components/Navigation/NavEnrollmentPeriodDropdown";
import { NavFormsList } from "src/components/Navigation/NavFormsList";
import { NavigationFoldButton } from "src/components/Navigation/NavigationFoldButton";
import { NavLevel } from "src/components/Navigation/NavLevel";
import { WithConfig } from "src/components/Permissions/WithConfig";
import { WithFeatureFlags } from "src/components/Permissions/WithFeatureFlags";
import { WithUserPermissions } from "src/components/Permissions/WithUserPermissions";
import { useBranding } from "src/components/Providers/BrandingProvider";
import { useFlags } from "src/components/Providers/FeatureFlagProvider";
import useCheckUserRole from "src/hooks/useCheckUserRole";
import { useMfaApi } from "src/hooks/useMfaApi";
import { useOrganization } from "src/hooks/useOrganization";
import { usePersistedDisclosure } from "src/hooks/usePersistedDisclosure";
import { getLogoAlt } from "src/schemas/Branding";
import * as Url from "src/services/url";
import { Organization } from "src/types/graphql";

export const Layout: React.FC = () => {
  const branding = useBranding();
  const { isOpen, onToggle } = usePersistedDisclosure({
    key: "LeftNavState",
    defaultIsOpen: true,
  });
  const location = useLocation();
  const organization = useOrganization();
  const flags = useFlags([
    "admin-dashboard",
    "avela-labs",
    "avela-labs-possible-student-duplicates",
    "staff-notifications",
    "org-config-self-service",
    "boundary-management",
  ]);
  const { isMfaEnabledRemoteData } = useMfaApi();
  const { isAvelaAdmin } = useCheckUserRole();
  const adminDashboardEnabled = flags["admin-dashboard"]?.enabled;
  const avelaLabsEnabled = flags["avela-labs"]?.enabled;
  const logo = (
    <>
      <NavigationFoldButton isOpen={isOpen} onToggle={onToggle} />
      {branding && (
        <Image
          src={branding.logos.whiteSvg}
          alt={getLogoAlt(branding)}
          height="3em"
          maxWidth="9rem"
        />
      )}
    </>
  );
  const {
    state: {
      formImportId,
      formTemplateId,
      formTemplateName,
      importFileUploadStatus,
    },
  } = useFormImportFlow();
  const OrgAdminNav = (org: Organization) => {
    const hasTransientLinks =
      formImportId &&
      formTemplateId &&
      location.pathname !== Url.OrgAdmin.FormImport.view(org, formTemplateId);
    // Note: In order for NavLevel's dividers to work correctly, hidden elements
    // must be literal `null`s.
    return (
      <LeftNav isOpen={isOpen}>
        <NavLevel level={0} id="workspace">
          {hasTransientLinks ? (
            <WithUserPermissions permissions={["navigation.admin.forms"]}>
              <NavGroup>
                <NavLinkItem
                  label={`Creating forms ${
                    formTemplateName ? `for ${formTemplateName}` : ""
                  }`}
                  leadIcon={undefined}
                  showSpinner={importFileUploadStatus === FlowStatus.STARTED}
                  url={Url.OrgAdmin.FormImport.view(org, formTemplateId)}
                />
              </NavGroup>
            </WithUserPermissions>
          ) : null}

          <NavGroup label="Enrollment">
            <NavEnrollmentPeriodDropdown organization={org} />
            <WithUserPermissions permissions={["navigation.admin.capacities"]}>
              <NavLinkItem
                url={Url.OrgAdmin.CapacitiesConfig.index(org)}
                label="Capacities"
                leadIcon={RiDashboard2Line}
              />
            </WithUserPermissions>
            <WithUserPermissions permissions={["navigation.admin.match"]}>
              <WithConfig configType="Match">
                <NavLinkItem
                  url={Url.OrgAdmin.Match.index(org)}
                  label="Match"
                  leadIcon={RiShuffleLine}
                />
              </WithConfig>
            </WithUserPermissions>
            <WithUserPermissions permissions={["navigation.admin.forms"]}>
              <NavFormsList organization={org} />
            </WithUserPermissions>
          </NavGroup>

          <WithUserPermissions
            permissions={[
              "navigation.admin.enrollment_periods",
              "navigation.admin.schools",
              "navigation.admin.users",
              "navigation.admin.team",
              "navigation.admin.messages",
              "navigation.admin.payments",
            ]}
          >
            <NavGroup label="General">
              {adminDashboardEnabled && (
                <NavLinkItem
                  url={Url.OrgAdmin.index(org)}
                  label="Home"
                  leadIcon={RiHomeLine}
                />
              )}
              <WithUserPermissions
                permissions={["navigation.admin.enrollment_periods"]}
              >
                <NavLinkItem
                  leadIcon={RiCalendarTodoLine}
                  label="Enrollment periods"
                  url={Url.OrgAdmin.Enrollments.index(org)}
                />
              </WithUserPermissions>
              <WithUserPermissions permissions={["navigation.admin.schools"]}>
                <NavLinkItem
                  leadIcon={RiBuilding4Line}
                  label="Schools"
                  url={Url.OrgAdmin.Schools.index(org)}
                />
              </WithUserPermissions>
              <WithUserPermissions permissions={["navigation.admin.users"]}>
                <NavLinkItem
                  leadIcon={RiUserLine}
                  label="Students"
                  url={Url.OrgAdmin.Students.index(org)}
                />
                <NavLinkItem
                  leadIcon={RiTeamLine}
                  label="Parents"
                  url={Url.OrgAdmin.Parents.index(org)}
                />
              </WithUserPermissions>
              <WithUserPermissions permissions={["navigation.admin.team"]}>
                <NavLinkItem
                  leadIcon={RiUserSettingsLine}
                  label="Teams & members"
                  url={Url.OrgAdmin.Team.index(org)}
                />
              </WithUserPermissions>
              <WithUserPermissions permissions={["navigation.admin.messages"]}>
                <NavLinkItem
                  leadIcon={RiChat2Line}
                  label="Message history"
                  url={Url.OrgAdmin.Messages.index(org)}
                />
              </WithUserPermissions>
              <WithUserPermissions
                permissions={["navigation.admin.data_services"]}
              >
                <NavLinkItem
                  leadIcon={RiAppsLine}
                  label="Data services"
                  url={Url.OrgAdmin.DataServices.index(org)}
                />
              </WithUserPermissions>
              <WithUserPermissions permissions={["navigation.admin.payments"]}>
                <WithConfig configType="Payments">
                  <NavLinkItem
                    leadIcon={RiCurrencyLine}
                    label="Payments"
                    url={Url.OrgAdmin.Payments.index(org)}
                  />
                </WithConfig>
              </WithUserPermissions>
              {avelaLabsEnabled ? (
                <NavLinkItem
                  leadIcon={RiFlaskLine}
                  label="Labs"
                  url={Url.OrgAdmin.AvelaLabs.aboutAvelaLabs(org)}
                />
              ) : null}
            </NavGroup>
          </WithUserPermissions>
          <NavGroup label="Help">
            <NavLinkItem
              leadIcon={RiQuestionLine}
              trailIcon={ExternalLinkIcon}
              trailIconVisibility="full"
              isExternal
              label="Avela support"
              url="https://avelahelp.zendesk.com/hc/en-us"
            />
          </NavGroup>
        </NavLevel>
        <NavLevel key="organization" level={1} id="organization">
          <NavGroup>
            <NavItem
              leadIcon={RiArrowLeftLine}
              label="Back to workspace"
              onClick={({ navigate }) => {
                navigate(Url.OrgAdmin.index(org));
              }}
            />
          </NavGroup>
          {flags["staff-notifications"]?.enabled ||
          (isMfaEnabledRemoteData.toNullable() !== false && !isAvelaAdmin) ? (
            <NavGroup label="Profile">
              {isMfaEnabledRemoteData.toNullable() !== false &&
              !isAvelaAdmin ? (
                <NavLinkItem
                  label="Security"
                  leadIcon={RiShieldKeyholeLine}
                  url={Url.OrgAdmin.UserProfile.security(org)}
                />
              ) : null}
              {flags["staff-notifications"]?.enabled ? (
                <NavLinkItem
                  label="Notifications"
                  leadIcon={RiNotification4Line}
                  url={Url.OrgAdmin.UserProfile.notifications(org)}
                />
              ) : null}
            </NavGroup>
          ) : null}
          <WithFeatureFlags featureFlags={["org-config-self-service"]}>
            <WithUserPermissions
              permissions={["navigation.admin.organization_settings"]}
            >
              <NavGroup label="Organization">
                {flags["boundary-management"]?.enabled ? (
                  <NavLinkItem
                    label="Boundaries"
                    leadIcon={RiTreasureMapLine}
                    url={Url.OrgAdmin.Org.boundaries(org)}
                  />
                ) : null}
                <NavLinkItem
                  label="Settings"
                  leadIcon={RiSoundModuleLine}
                  url={Url.OrgAdmin.Org.settings(org)}
                />
              </NavGroup>
            </WithUserPermissions>
          </WithFeatureFlags>
          <NavGroup label="Help">
            <NavLinkItem
              leadIcon={RiQuestionLine}
              trailIcon={ExternalLinkIcon}
              trailIconVisibility="full"
              isExternal
              label="Avela support"
              url="https://avelahelp.zendesk.com/hc/en-us"
            />
          </NavGroup>
        </NavLevel>
      </LeftNav>
    );
  };
  return (
    <BaseLayout
      header={<Header logo={logo} />}
      leftNav={organization
        .map<React.ReactNode>((org: Organization) => {
          return OrgAdminNav(org);
        })
        .withDefault(null)}
    />
  );
};
